import { Component } from '@theme/component';
import { ThemeEvents, MediaStartedPlayingEvent } from '@theme/events';
import { DialogCloseEvent } from '@theme/dialog';

const PRODUCT_VIDEO_SOUND_STYLE_ID = 'nomitail-product-video-sound-style';
const PRODUCT_VIDEO_SOUND_BUTTON_CLASS = 'nomitail-video-sound-toggle';

function injectProductVideoSoundStyle() {
  if (document.getElementById(PRODUCT_VIDEO_SOUND_STYLE_ID)) return;

  const style = document.createElement('style');
  style.id = PRODUCT_VIDEO_SOUND_STYLE_ID;
  style.textContent = [
    '.product-media-container--video deferred-media > .video-sound-toggle{display:none!important;width:0!important;height:0!important;min-width:0!important;min-height:0!important;padding:0!important;margin:0!important;opacity:0!important;pointer-events:none!important;}',
    '.product-media-container--video deferred-media{position:relative;}',
    `.product-media-container--video deferred-media > .${PRODUCT_VIDEO_SOUND_BUTTON_CLASS}{position:absolute!important;right:14px!important;top:14px!important;left:auto!important;bottom:auto!important;z-index:12!important;display:inline-flex!important;align-items:center!important;justify-content:center!important;width:42px!important;height:42px!important;min-width:42px!important;min-height:42px!important;max-width:42px!important;max-height:42px!important;padding:0!important;margin:0!important;color:#fff!important;background:rgba(0,0,0,.72)!important;border:1px solid rgba(255,255,255,.34)!important;border-radius:999px!important;box-shadow:0 10px 26px rgba(0,0,0,.24)!important;cursor:pointer!important;appearance:none!important;pointer-events:auto!important;}`,
    `.product-media-container--video deferred-media > .${PRODUCT_VIDEO_SOUND_BUTTON_CLASS}:hover{background:rgba(0,0,0,.86)!important;border-color:rgba(255,255,255,.55)!important;}`,
    `.product-media-container--video deferred-media > .${PRODUCT_VIDEO_SOUND_BUTTON_CLASS} svg{width:21px!important;height:21px!important;fill:none!important;stroke:currentColor!important;stroke-linecap:round!important;stroke-linejoin:round!important;stroke-width:1.9!important;}`,
    `.${PRODUCT_VIDEO_SOUND_BUTTON_CLASS} .nomitail-sound-on{display:none!important;}`,
    `.${PRODUCT_VIDEO_SOUND_BUTTON_CLASS}.is-unmuted .nomitail-sound-off{display:none!important;}`,
    `.${PRODUCT_VIDEO_SOUND_BUTTON_CLASS}.is-unmuted .nomitail-sound-on{display:inline-flex!important;}`,
    `@media(max-width:749px){.product-media-container--video deferred-media > .${PRODUCT_VIDEO_SOUND_BUTTON_CLASS}{right:10px!important;top:10px!important;width:38px!important;height:38px!important;min-width:38px!important;min-height:38px!important;max-width:38px!important;max-height:38px!important}.product-media-container--video deferred-media > .${PRODUCT_VIDEO_SOUND_BUTTON_CLASS} svg{width:19px!important;height:19px!important}}`,
  ].join('');
  document.head.appendChild(style);
}

function productVideoSoundButtonMarkup() {
  return [
    '<span class="nomitail-sound-off" aria-hidden="true">',
    '<svg viewBox="0 0 24 24" focusable="false"><path d="M4 9v6h4l5 4V5L8 9H4Z"></path><path d="m17 9 4 4m0-4-4 4"></path></svg>',
    '</span>',
    '<span class="nomitail-sound-on" aria-hidden="true">',
    '<svg viewBox="0 0 24 24" focusable="false"><path d="M4 9v6h4l5 4V5L8 9H4Z"></path><path d="M16 8.5a5 5 0 0 1 0 7"></path><path d="M18.5 6a8.5 8.5 0 0 1 0 12"></path></svg>',
    '</span>',
  ].join('');
}

/**
 * A deferred media element
 * @typedef {Object} Refs
 * @property {HTMLElement} deferredMediaPlayButton - The button to show the deferred media content
 * @property {HTMLElement} toggleMediaButton - The button to toggle the media
 * @property {HTMLElement} soundToggleButton - The button to toggle video sound
 *
 * @extends {Component<Refs>}
 */
class DeferredMedia extends Component {
  /** @type {boolean} */
  isPlaying = false;

  /** @type {boolean} */
  soundToggleInitialized = false;

  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();
    const signal = this.#abortController.signal;
    // If we're to use deferred media for images, we will need to run this only when it's not an image type media
    document.addEventListener(ThemeEvents.mediaStartedPlaying, this.pauseMedia.bind(this), { signal });
    window.addEventListener(DialogCloseEvent.eventName, this.pauseMedia.bind(this), { signal });
    this.setupSoundToggleButton();
    this.updateSoundToggle(this.querySelector('video')?.muted ?? true);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  /**
   * Updates the visual hint for play/pause state
   * @param {boolean} isPlaying - Whether the video is currently playing
   */
  updatePlayPauseHint(isPlaying) {
    const toggleMediaButton = this.refs.toggleMediaButton;
    if (toggleMediaButton instanceof HTMLElement) {
      toggleMediaButton.classList.remove('hidden');
      const playIcon = toggleMediaButton.querySelector('.icon-play');
      if (playIcon) playIcon.classList.toggle('hidden', isPlaying);
      const pauseIcon = toggleMediaButton.querySelector('.icon-pause');
      if (pauseIcon) pauseIcon.classList.toggle('hidden', !isPlaying);
    }
  }

  /**
   * Updates the visual state for the mute/unmute button.
   * @param {boolean} isMuted - Whether the video is currently muted
   */
  updateSoundToggle(isMuted) {
    const soundToggleButton = this.querySelector(`.${PRODUCT_VIDEO_SOUND_BUTTON_CLASS}`);
    if (soundToggleButton instanceof HTMLElement) {
      soundToggleButton.classList.toggle('is-muted', isMuted);
      soundToggleButton.classList.toggle('is-unmuted', !isMuted);
      soundToggleButton.setAttribute('aria-label', isMuted ? 'Turn video sound on' : 'Turn video sound off');
      soundToggleButton.setAttribute('aria-pressed', String(!isMuted));
    }
  }

  /**
   * Removes the former custom sound button; native controls provide sound.
   */
  setupSoundToggleButton() {
    this.querySelectorAll(`.video-sound-toggle, .${PRODUCT_VIDEO_SOUND_BUTTON_CLASS}`).forEach((button) => button.remove());
    this.soundToggleInitialized = true;
  }

  /**
   * Shows the deferred media content
   */
  showDeferredMedia = () => {
    this.loadContent(true);
    this.isPlaying = true;
    this.updatePlayPauseHint(this.isPlaying);
  };

  /**
   * Loads the content
   * @param {boolean} [focus] - Whether to focus the content
   */
  loadContent(focus = true) {
    if (this.getAttribute('data-media-loaded')) return;

    this.dispatchEvent(new MediaStartedPlayingEvent(this));

    const content = this.querySelector('template')?.content.firstElementChild?.cloneNode(true);

    if (!content) return;

    this.setAttribute('data-media-loaded', 'true');
    this.appendChild(content);

    if (focus && content instanceof HTMLElement) {
      content.focus();
    }

    this.refs.deferredMediaPlayButton?.classList.add('deferred-media__playing');

    if (content instanceof HTMLVideoElement) {
      const isProductGalleryVideo = Boolean(this.closest('.product-media-container--video'));
      if (isProductGalleryVideo) {
        content.controls = true;
        content.setAttribute('controls', 'controls');
        content.setAttribute('controlsList', 'nodownload');
        content.setAttribute('oncontextmenu', 'return false;');
        content.autoplay = true;
        content.setAttribute('autoplay', '');
        content.muted = true;
        content.defaultMuted = true;
        content.setAttribute('muted', '');
      }

      if (isProductGalleryVideo || focus || content.getAttribute('autoplay') || this.hasAttribute('autoplay')) {
        // force playback for Safari after deferred media is revealed
        const playPromise = content.play();
        if (playPromise instanceof Promise) {
          playPromise.catch(() => {});
        }
      }
      this.setupSoundToggleButton();
      this.updateSoundToggle(content.muted);
    }
  }

  /**
   * Toggle play/pause state of the media
   */
  toggleMedia() {
    if (this.isPlaying) {
      this.pauseMedia();
    } else {
      this.playMedia();
    }
  }

  /**
   * Toggle mute/unmute for Shopify-hosted videos.
   * @param {Event} [event]
   */
  toggleSound(event) {
    event?.preventDefault();
    event?.stopPropagation();
    event?.stopImmediatePropagation?.();

    let video = this.querySelector('video');
    if (!video && !this.getAttribute('data-media-loaded')) {
      this.loadContent(false);
      video = this.querySelector('video');
    }

    if (!(video instanceof HTMLVideoElement)) return;

    const shouldMute = !video.muted;
    video.muted = shouldMute;
    video.defaultMuted = shouldMute;

    if (shouldMute) {
      video.setAttribute('muted', '');
      this.updateSoundToggle(true);
      return;
    }

    video.removeAttribute('muted');
    const playPromise = video.play();
    this.isPlaying = true;
    this.updatePlayPauseHint(this.isPlaying);
    this.updateSoundToggle(false);

    if (playPromise instanceof Promise) {
      playPromise.catch(() => {
        video.muted = true;
        video.defaultMuted = true;
        video.setAttribute('muted', '');
        this.updateSoundToggle(true);
      });
    }
  }

  playMedia() {
    /** @type {HTMLIFrameElement | null} */
    const iframe = this.querySelector('iframe[data-video-type]');
    if (iframe) {
      iframe.contentWindow?.postMessage(
        iframe.dataset.videoType === 'youtube'
          ? '{"event":"command","func":"playVideo","args":""}'
          : '{"method":"play"}',
        '*'
      );
    } else {
      this.querySelector('video')?.play();
    }
    this.isPlaying = true;
    this.updatePlayPauseHint(this.isPlaying);
  }

  /**
   * Pauses the media
   */
  pauseMedia() {
    /** @type {HTMLIFrameElement | null} */
    const iframe = this.querySelector('iframe[data-video-type]');

    if (iframe) {
      iframe.contentWindow?.postMessage(
        iframe.dataset.videoType === 'youtube'
          ? '{"event":"command","func":"' + 'pauseVideo' + '","args":""}'
          : '{"method":"pause"}',
        '*'
      );
    } else {
      this.querySelector('video')?.pause();
    }
    this.isPlaying = false;

    // If we've already revealed the deferred media, we should toggle the play/pause hint
    if (this.getAttribute('data-media-loaded')) {
      this.updatePlayPauseHint(this.isPlaying);
    }
  }
}

if (!customElements.get('deferred-media')) {
  customElements.define('deferred-media', DeferredMedia);
}

/**
 * A product model
 */
class ProductModel extends DeferredMedia {
  #abortController = new AbortController();

  loadContent() {
    super.loadContent();

    Shopify.loadFeatures([
      {
        name: 'model-viewer-ui',
        version: '1.0',
        onLoad: this.setupModelViewerUI.bind(this),
      },
    ]);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  pauseMedia() {
    super.pauseMedia();
    this.modelViewerUI?.pause();
  }

  playMedia() {
    super.playMedia();
    this.modelViewerUI?.play();
  }

  /**
   * @param {Error[]} errors
   */
  async setupModelViewerUI(errors) {
    if (errors) return;

    if (!Shopify.ModelViewerUI) {
      await this.#waitForModelViewerUI();
    }

    if (!Shopify.ModelViewerUI) return;

    const element = this.querySelector('model-viewer');
    if (!element) return;

    const signal = this.#abortController.signal;

    this.modelViewerUI = new Shopify.ModelViewerUI(element);
    if (!this.modelViewerUI) return;

    this.playMedia();

    // Track pointer events to detect taps
    let pointerStartX = 0;
    let pointerStartY = 0;

    element.addEventListener(
      'pointerdown',
      (/** @type {PointerEvent} */ event) => {
        pointerStartX = event.clientX;
        pointerStartY = event.clientY;
      },
      { signal }
    );

    element.addEventListener(
      'click',
      (/** @type {PointerEvent} */ event) => {
        const distanceX = Math.abs(event.clientX - pointerStartX);
        const distanceY = Math.abs(event.clientY - pointerStartY);
        const totalDistance = Math.sqrt(distanceX * distanceX + distanceY * distanceY);

        // Try to ensure that this is a tap, not a drag.
        if (totalDistance < 10) {
          // When the model is paused, it has its own button overlay for playing the model again.
          // If we're receiving a click event, it means the model is playing, all we can do is pause it.
          this.pauseMedia();
        }
      },
      { signal }
    );
  }

  /**
   * Waits for Shopify.ModelViewerUI to be defined.
   * This seems to be necessary for Safari since Shopify.ModelViewerUI is always undefined on the first try.
   * @returns {Promise<void>}
   */
  async #waitForModelViewerUI() {
    const maxAttempts = 10;
    const interval = 50;

    for (let i = 0; i < maxAttempts; i++) {
      if (Shopify.ModelViewerUI) {
        return;
      }
      await new Promise((resolve) => setTimeout(resolve, interval));
    }
  }
}

if (!customElements.get('product-model')) {
  customElements.define('product-model', ProductModel);
}
